"use client";

import { useState } from "react";
import { LiveAvatarSession } from "./LiveAvatarSession";
import { apiUrl } from "../lib/api";

export const LiveAvatarDemo = () => {
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleStart = async () => {
    setError(null);
    setLoading(true);
    try {
      const res = await fetch(apiUrl("/api/start-session"), { method: "POST" });
      const data = await res.json();
      if (!res.ok) { setError(data.error ?? "Failed to start session"); return; }
      setSessionToken(data.session_token);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const onSessionStopped = () => setSessionToken(null);

  if (sessionToken) {
    return (
      <LiveAvatarSession
        mode="CUSTOM"
        sessionAccessToken={sessionToken}
        onSessionStopped={onSessionStopped}
      />
    );
  }

  return (
    <div className="relative flex flex-col items-center justify-center gap-8 min-h-screen w-full overflow-hidden">
      {/* Ambient background */}
      <div className="absolute top-[-30%] left-[-15%] w-[700px] h-[700px] bg-violet-600/10 rounded-full blur-[180px] pointer-events-none" />
      <div className="absolute bottom-[-30%] right-[-15%] w-[600px] h-[600px] bg-cyan-500/8 rounded-full blur-[160px] pointer-events-none" />

      {/* Subtle grid */}
      <div
        className="absolute inset-0 opacity-[0.025] pointer-events-none"
        style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,.15) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.15) 1px, transparent 1px)`,
          backgroundSize: "50px 50px",
        }}
      />

      {/* Content */}
      <div className="relative flex flex-col items-center gap-3 text-center z-10">
        {/* Animated icon */}
        <div className="relative mb-2">
          <div className="absolute inset-0 bg-gradient-to-br from-violet-500 to-cyan-500 rounded-full blur-2xl opacity-30 animate-pulse" />
          <div className="relative w-16 h-16 rounded-full bg-gradient-to-br from-violet-500/20 to-cyan-500/20 border border-white/10 flex items-center justify-center backdrop-blur-xl">
            <svg className="w-7 h-7 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
            </svg>
          </div>
        </div>

        <h1 className="text-2xl font-semibold tracking-tight text-white">
          বাংলা এআই সহকারী
        </h1>
        <p className="text-white/35 text-sm max-w-xs leading-relaxed">
          আপনার ডিজিটাল সঙ্গী
        </p>
      </div>

      {error && (
        <div className="relative z-10 flex items-center gap-2 px-4 py-2.5 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm max-w-xs">
          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
          </svg>
          {error}
        </div>
      )}

      <button
        onClick={handleStart}
        disabled={loading}
        className="relative z-10 group flex items-center gap-3 px-8 py-3.5 rounded-2xl font-medium text-sm text-white overflow-hidden transition-all duration-300 active:scale-[0.97] disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {/* Button background */}
        <div className="absolute inset-0 bg-gradient-to-r from-violet-600 to-cyan-500 transition-all duration-300 group-hover:from-violet-500 group-hover:to-cyan-400" />
        {/* Hover glow */}
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-r from-violet-400/40 to-cyan-400/40 blur-xl -z-10" />
        {/* Shimmer */}
        <div className="absolute inset-0 opacity-0 group-hover:opacity-30 transition-opacity duration-500 bg-gradient-to-r from-transparent via-white/30 to-transparent -skew-x-12 translate-x-[-100%] group-hover:translate-x-[200%]" style={{ transition: "transform 0.8s ease, opacity 0.3s ease" }} />

        {loading ? (
          <span className="relative flex items-center gap-2.5">
            <div className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
            Connecting…
          </span>
        ) : (
          <span className="relative flex items-center gap-2.5">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              <polygon points="5,3 19,12 5,21" />
            </svg>
            Start Session
          </span>
        )}
      </button>

      {/* Bottom status indicator */}
      <div className="relative z-10 flex items-center gap-2 text-xs text-white/25 mt-2">
        <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
        System ready
      </div>
    </div>
  );
};
